import {useMix} from '@hconnect/tuc-processes/fields/mix/useMix'
import {useOrderProcessFormContext} from '@hconnect/tuc-processes/form/useOrderProcessFormContext'
import {useMemo} from 'react'

import {useJobSite} from '../../../../../orderIntake/contexts/JobSiteContext'
import {getTimeRelatedParams} from '../../../utils/timeRelatedParams'

import {SlotManagementOptions} from './types'
import {useAvailableSlots, AvailableSlotsParams} from './useAvailableSlots'
import {useFeatureSlotManagementForRmcOrderIntakeEnabled} from './useFeatureFlag'

/**
 * Main slot management hook that orchestrates all slot-related functionality
 * Integrates with:
 * - Real API for slot data (useAvailableSlots)
 * - Feature flags for slot management enablement
 * - Form context for order parameters
 * - Handles loading states and error states
 * - Provides slot availability and validation
 */
export const useSlotManagement = (): SlotManagementOptions => {
  // Get feature flag for slot management enablement
  const slotManagementEnabled = useFeatureSlotManagementForRmcOrderIntakeEnabled()

  // Get job site data for siteNumber
  const {jobSite} = useJobSite()

  // Get form context for order parameters
  const {watch} = useOrderProcessFormContext()
  const {selectedContract, selectedMaterial} = useMix()
  const [
    quantity,
    plusLoadExpected,
    timeOnJobSite,
    spacingOrFlowRate,
    dateTime,
    unloadingMethod,
    truckType
  ] = watch([
    'quantity',
    'plusLoadExpected',
    'timeOnJobSite',
    'spacingOrFlowRate',
    'dateTime',
    'unloadingMethod',
    'truckType'
  ])

  // Build API parameters from form data
  const apiParams: AvailableSlotsParams = useMemo(() => {
    // Get time-related parameters using the shared utility
    const timeRelatedParams = getTimeRelatedParams(timeOnJobSite, spacingOrFlowRate)

    // Transform API format to slot API format
    const slotTimeParams: {
      loadSpacing?: string
      flowRate?: string
      totalTimeOnJobSite?: string
    } = {}

    if (timeRelatedParams.timeOnJobSite) {
      slotTimeParams.totalTimeOnJobSite = timeRelatedParams.timeOnJobSite
    }
    if (timeRelatedParams.loadSpace) {
      slotTimeParams.loadSpacing = timeRelatedParams.loadSpace
    }
    if (timeRelatedParams.flowRate) {
      slotTimeParams.flowRate = timeRelatedParams.flowRate.toString()
    }

    return {
      shippingDate: dateTime?.[0] || new Date().toISOString(),
      contractNumber: selectedContract?.contractNumber || '',
      contractItemNumber: selectedMaterial?.contractItemPositionNumber || '',
      siteNumber: jobSite?.siteNumber || '', // shouldn't we use siteId?
      quantity: quantity.type === 'volume' ? quantity.volume : quantity.minQuantity, // In quantity mode use exact m³, in fullLoads mode use minQuantity for slot validation
      ...(truckType && {truckType}),
      ...(unloadingMethod?.unloadingMethodCode && {
        unloadingMethod: unloadingMethod.unloadingMethodCode
      }),
      orgUnitId: selectedContract?.orgUnitId || '',
      ...slotTimeParams,
      plusLoadExpected
    }
  }, [
    dateTime,
    selectedContract?.contractNumber,
    selectedContract?.orgUnitId,
    selectedMaterial?.contractItemPositionNumber,
    jobSite?.siteNumber,
    quantity,
    plusLoadExpected,
    truckType,
    unloadingMethod?.unloadingMethodCode,
    spacingOrFlowRate,
    timeOnJobSite
  ])

  // Fetch available slots from API (only if slot management is enabled)
  const availableSlotsResult = useAvailableSlots(apiParams)
  const {
    allTimeSlots,
    availableSlots,
    offHoursSlots,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
    selectedSlot,
    isOutOfAutoConfirmLeadTime,
    instantApprovalUnavailable,
    showBannerWhenSlotIsUnavailable,
    isSelectedSlotAvailable
  } = availableSlotsResult

  // Combine all slot management options
  const slotManagementOptions = useMemo((): SlotManagementOptions => {
    // If slot management is disabled, return minimal options for time picker mode
    if (!slotManagementEnabled) {
      return {
        isLoading: false,
        isFetching: false,
        isError: false,
        error: null,
        refetch: () => {
          // No-op when slot management is disabled
        },
        isOutOfAutoConfirmLeadTime: false,
        instantApprovalUnavailable: false,
        allTimeSlots: [],
        availableSlots: [],
        offHoursSlots: [],
        selectedSlot: undefined,
        slotManagementEnabled: false,
        showBannerWhenSlotIsUnavailable: false,
        isSelectedSlotAvailable: true,
        isMaxAutoConfirmQuantityExceeded: false
      }
    }

    // Return full slot management options when enabled
    return {
      isLoading,
      isFetching,
      isError,
      error,
      refetch,
      isOutOfAutoConfirmLeadTime,
      instantApprovalUnavailable,
      allTimeSlots,
      availableSlots,
      offHoursSlots,
      selectedSlot,
      slotManagementEnabled: true,
      showBannerWhenSlotIsUnavailable,
      isSelectedSlotAvailable,
      isMaxAutoConfirmQuantityExceeded: false // TODO: Implement actual logic based on quantity validation after adding maxAutoConfirmableQuantity to QuantityConfiguration
    }
  }, [
    slotManagementEnabled,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
    isOutOfAutoConfirmLeadTime,
    instantApprovalUnavailable,
    allTimeSlots,
    availableSlots,
    offHoursSlots,
    selectedSlot,
    showBannerWhenSlotIsUnavailable,
    isSelectedSlotAvailable
  ])

  return slotManagementOptions
}
